// ./context/AuthContext.js

import { createContext, useReducer, useEffect } from 'react';
import { onAuthStateChanged, getAuth } from 'firebase/auth';
import { doc, getDoc, getFirestore } from 'firebase/firestore';

export const AuthContext = createContext();

const INITIAL_STATE = {
  currentUser: JSON.parse(localStorage.getItem('user')) || null,
  isLoading: true,
};

const AuthReducer = (state, action) => {
  switch (action.type) {
    case 'LOGIN': {
      return {
        ...state,
        currentUser: action.payload,
        isLoading: false,
      };
    }
    case 'LOGOUT': {
      return {
        ...state,
        currentUser: null,
        isLoading: false,
      };
    }
    case 'SET_LOADING': {
      return {
        ...state,
        isLoading: action.payload,
      };
    }
    default:
      return state;
  }
};

export const AuthContextProvider = ({ children }) => {
  const [state, dispatch] = useReducer(AuthReducer, INITIAL_STATE);
  const auth = getAuth();
  const db = getFirestore();

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      console.log(
        'Firebase Auth State Changed:',
        firebaseUser?.uid || 'No user'
      );

      if (firebaseUser) {
        try {
          // User is signed in with Firebase
          // Check if user exists in staff collection
          const staffDocRef = doc(db, 'staff', firebaseUser.uid);
          const staffDoc = await getDoc(staffDocRef);

          if (staffDoc.exists()) {
            const staffData = staffDoc.data();
            const userData = {
              uid: firebaseUser.uid,
              email: firebaseUser.email,
              staffData: staffData,
            };

            // Update both context and localStorage
            dispatch({
              type: 'LOGIN',
              payload: userData,
            });

            localStorage.setItem('user', JSON.stringify(userData));
          } else {
            // User not found in staff collection - should not happen for logged-in users
            console.log('User not found in staff collection, logging out');
            await auth.signOut();
            dispatch({ type: 'LOGOUT' });
            localStorage.removeItem('user');
            localStorage.removeItem('authToken');
          }
        } catch (error) {
          console.error('Error validating user session:', error);
          dispatch({ type: 'LOGOUT' });
          localStorage.removeItem('user');
          localStorage.removeItem('authToken');
        }
      } else {
        // User is signed out
        console.log('User signed out, clearing local state');
        dispatch({ type: 'LOGOUT' });
        localStorage.removeItem('user');
        localStorage.removeItem('authToken');
      }
    });

    return () => unsubscribe();
  }, [auth, db]);

  // Update localStorage when currentUser changes
  useEffect(() => {
    if (state.currentUser) {
      localStorage.setItem('user', JSON.stringify(state.currentUser));
    } else {
      localStorage.removeItem('user');
      localStorage.removeItem('authToken');
    }
  }, [state.currentUser]);

  return (
    <AuthContext.Provider
      value={{
        currentUser: state.currentUser,
        isLoading: state.isLoading,
        dispatch,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
